// FindAnglesFromTwoSidesAndOneAngleTask.js
// Dane: a (naprzeciw α), b (naprzeciw β) i kąt α. Oblicz: sin β, β, γ.
// Tw. sinusów: a/sinα = b/sinβ  ⇒  sinβ = (b·sinα)/a;   β = arcsin(sinβ);  γ = 180° − α − β
// Generator unika dwuznaczności SSA: wymusza β ≤ α − 1° i γ ≥ 8°.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  parseFractionLikeInput as parseRootFractionInput
} from "../../../../components/inputs/FractionInput";


/* =======================  HELPERY  ======================= */

function sinLatex(angle) {
  switch (angle) {
    case 30:
    case 150:
      return "\\dfrac{1}{2}";
    case 45:
    case 135:
      return "\\dfrac{\\sqrt{2}}{2}";
    case 60:
    case 120:
      return "\\dfrac{\\sqrt{3}}{2}";
    case 90:
      return "1";
    default:
      return "";
  }
}

function sinNumeric(angle) {
  switch (angle) {
    case 30:
    case 150:
      return 0.5;
    case 45:
    case 135:
      return Math.SQRT2 / 2;
    case 60:
    case 120:
      return Math.sqrt(3) / 2;
    case 90:
      return 1;
    default:
      return Math.sin((angle * Math.PI) / 180);
  }
}

function formatDecimalLatex(x, digits = 2) {
  const s = x.toFixed(digits);
  const trimmed = s.replace(/(\.\d*?[1-9])0+$/,"$1").replace(/\.0+$/,"");
  return trimmed.replace(".", "{,}");
}



// wstaw '*' między cyfrą a √ / \sqrt / '('; liczba mieszana -> ułamek
function preprocessRaw(raw) {
  if (raw == null) return "";
  let s = String(raw).trim();

  const m = s.match(/^\s*([+-]?\d+)\s+(\d+)\s*\/\s*(\d+)\s*$/);
  if (m) {
    const A = parseInt(m[1], 10), B = parseInt(m[2], 10), C = parseInt(m[3], 10);
    const improper = A >= 0 ? (A * C + B) : (A * C - B);
    return `${improper}/${C}`;
  }

  s = s.replace(/\u2212/g, "-");
  s = s.replace(/\s+/g, "").replace(",", ".");
  s = s.replace(/(\d)(?=(√|\\sqrt|\())/g, "$1*");
  return s;
}

/* =======================  GENERATOR  ======================= */

export function generateTask() {
  // α tylko z kątów „tabelkowych”, ale BEZ 90° (unikamy trójkątów prostokątnych)
  const angles = [30, 45, 60, 120, 135, 150];
  const alpha = angles[Math.floor(Math.random() * angles.length)];
  const sA = sinNumeric(alpha);

  const minA = 8, maxA = 24;
  const a = Math.floor(Math.random() * (maxA - minA + 1)) + minA;

  const minGamma = 8;  // wygodny trzeci kąt
  const margin   = 1;  // β ≤ α − 1°  ⇒ brak dwuznaczności SSA

  const isRightish = (x) => Math.round(x) === 90;

  let b = null, beta = null, gamma = null, tries = 0;

  while (tries++ < 200) {
    const r = 0.5 + Math.random() * 0.8; // 0.5..1.3 * a
    b = Math.max(3, Math.round(a * r));

    const sinB = (b * sA) / a;
    if (sinB <= 0 || sinB >= 1) continue;

    const beta1 = (Math.asin(sinB) * 180) / Math.PI; // kąt ostry
    if (beta1 > alpha - margin) continue;            // anty-SSA

    const gamma1 = 180 - alpha - beta1;
    if (gamma1 < minGamma) continue;

    // ⛔ unikamy trójkątów prostokątnych po zaokrągleniu
    if (isRightish(beta1) || isRightish(gamma1)) continue;

    beta = beta1;
    gamma = gamma1;
    break;
  }

  // fallback – gdyby nie znalazło w pętli
  if (beta == null) {
    b = Math.max(3, Math.round(0.8 * a));
    const sinB = Math.min(0.9, Math.max(0.1, (b * sA) / a));
    beta  = Math.min(alpha - margin, (Math.asin(sinB) * 180) / Math.PI);
    gamma = 180 - alpha - beta;

    // jeśli przypadkiem „uderzyło” w 90° po zaokrągleniu – lekko skoryguj
    if (isRightish(beta)) {
      beta = Math.max(1, beta - 1);
      gamma = 180 - alpha - beta;
    }
    if (isRightish(gamma)) {
      beta = Math.min(alpha - margin, beta + 1);
      gamma = 180 - alpha - beta;
    }
  }

  // zapisujemy kąty jako CAŁE stopnie (pod tablice/odpowiedzi)
  return {
    type: "FindAnglesFromTwoSidesAndOneAngleTask",
    data: {
      type: "FindAnglesFromTwoSidesAndOneAngleTask",
      a, b, alpha,
      beta:  Math.round(beta),
      gamma: Math.round(gamma),
    },
  };
}


/* =======================  RENDER: TREŚĆ  ======================= */

export function renderTask({ data }) {
  const { a, b, alpha } = data;
  return (
    <div style={{ marginTop: "6px", marginBottom: "6px" }}>
      <div>
        W trójkącie <InlineMath math={'ABC'} /> dane są boki{" "}
        <InlineMath math={`a=${a}`} /> i <InlineMath math={`b=${b}`} /> oraz kąt{" "}
        <InlineMath math={`\\alpha=${alpha}^\\circ`} />. Oblicz kolejno:{" "}
        <InlineMath math={'\\sin\\beta'} />, <InlineMath math={'\\beta'} /> i{" "}
        <InlineMath math={'\\gamma'} />.
      </div>
    </div>
  );
}

/* =======================  INPUT  ======================= */

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const val = userAnswer ?? { sinb: { raw: "" }, beta: { raw: "" }, gamma: { raw: "" } };

  const group = { display: "grid", gap: "6px" };

  return (
    <div style={{ display: "grid", gap: "14px", marginTop: "6px" }}>
      {/* sin β */}
      <div style={group}>
        <InlineMath math={'\\sin\\beta ='} />
        <FractionInput
          value={val.sinb}
          onChange={(v) => onChange({ ...(userAnswer ?? {}), sinb: v })}
          disabled={disabled}
          label={"\\sin\\beta"}
          /* bez suffixu – to nie są stopnie */
        />
      </div>

      {/* β w stopniach */}
      <div style={group}>
        <InlineMath math={'\\beta ='} />
        <FractionInput
          value={val.beta}
          onChange={(v) => onChange({ ...(userAnswer ?? {}), beta: v })}
          disabled={disabled}
          label={"\\beta"}                 // grecka litera wprost
          suffixLatex="^\circ"    // symbol stopni w podglądzie komponentu
        />
      </div>

      {/* γ w stopniach */}
      <div style={group}>
        <InlineMath math={'\\gamma ='} />
        <FractionInput
          value={val.gamma}
          onChange={(v) => onChange({ ...(userAnswer ?? {}), gamma: v })}
          disabled={disabled}
          label={"\\gamma"}
          suffixLatex="^\circ"
        />
      </div>
    </div>
  );
}



/* =======================  CHECK  ======================= */

export function checkAnswer({ data }, userAnswer) {
  const { a, b, alpha } = data;

  const sinB_exp = (b * sinNumeric(alpha)) / a;
  let beta_exp = (Math.asin(Math.min(1, Math.max(0, sinB_exp))) * 180) / Math.PI;
  let gamma_exp = 180 - alpha - beta_exp;

  // SSA fallback – jak wcześniej
  if (gamma_exp <= 0) {
    const beta2 = 180 - beta_exp;
    if (beta2 > 0 && beta2 < 180 - alpha) {
      beta_exp = beta2;
      gamma_exp = 180 - alpha - beta_exp;
    }
  }

  const sinbRaw = userAnswer?.sinb?.raw ?? userAnswer?.sinb;
  const betaRaw = userAnswer?.beta?.raw ?? userAnswer?.beta;
  const gammaRaw = userAnswer?.gamma?.raw ?? userAnswer?.gamma;

  const sinbParsed = sinbRaw != null
    ? parseRootFractionInput(preprocessRaw(sinbRaw))
    : null;

  const betaParsed = betaRaw != null
    ? parseRootFractionInput(preprocessRaw(betaRaw))
    : null;

  const gammaParsed = gammaRaw != null
    ? parseRootFractionInput(preprocessRaw(gammaRaw))
    : null;

  // --- punkt 1: sinus ---
  const okSin =
    sinbParsed != null &&
    Number.isFinite(sinbParsed) &&
    Math.abs(sinbParsed - sinB_exp) <= 0.01;

  // --- punkt 2: oba kąty razem ---
  const betaTarget = Math.round(beta_exp);
  const gammaTarget = Math.round(gamma_exp);

  const okAngles =
    betaParsed != null &&
    gammaParsed != null &&
    Number.isFinite(betaParsed) &&
    Number.isFinite(gammaParsed) &&
    Math.abs(betaParsed - betaTarget) <= 1 &&
    Math.abs(gammaParsed - gammaTarget) <= 1;

  const score = (okSin ? 1 : 0) + (okAngles ? 1 : 0);

  return {
    isCorrect: score === 2,
    score,
    maxScore: 2,
    parts: {
      sinb: okSin,
      angles: okAngles,
    },
  };
}

/* =======================  ANSWER  ======================= */

export function renderAnswer({ data }) {
  const { a, b, alpha } = data;
  const sinB = (b * sinNumeric(alpha)) / a;
  let beta = Math.asin(Math.min(1, Math.max(0, sinB))) * 180 / Math.PI;
  let gamma = 180 - alpha - beta;
  if (gamma <= 0) {
    const beta2 = 180 - beta;
    if (beta2 > 0 && beta2 < 180 - alpha) {
      beta = beta2;
      gamma = 180 - alpha - beta;
    }
  }

  return (
    <div style={{ marginTop: "6px", marginBottom: "6px" }}>
      <div><InlineMath math={`\\sin\\beta \\approx ${formatDecimalLatex(sinB, 3)}`} /></div>
      <div><InlineMath math={`\\beta \\approx ${Math.round(beta)}^\\circ`} /></div>
      <div><InlineMath math={`\\gamma \\approx ${Math.round(gamma)}^\\circ`} /></div>
    </div>
  );
}


/* =======================  EXPLANATION  ======================= */

export function renderExplanation({ data }) {
  const { a, b, alpha } = data;

  const sA_latex = sinLatex(alpha) || formatDecimalLatex(sinNumeric(alpha), 3);
  const sA_num   = sinNumeric(alpha);
  const sinB     = (b * sA_num) / a;

  let beta = Math.asin(Math.min(1, Math.max(0, sinB))) * 180 / Math.PI;
  let gamma = 180 - alpha - beta;
  if (gamma <= 0) {
    const beta2 = 180 - beta;
    if (beta2 > 0 && beta2 < 180 - alpha) {
      beta = beta2;
      gamma = 180 - alpha - beta;
    }
  }

  return (
    <div style={{ textAlign: "left", marginTop: "8px", lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> Twierdzenie sinusów i izolacja <InlineMath math={'\\sin\\beta'} />:</div>
      <BlockMath math={`\\dfrac{a}{\\sin\\alpha} = \\dfrac{b}{\\sin\\beta} \\quad\\Rightarrow\\quad \\sin\\beta = \\dfrac{b\\,\\sin\\alpha}{a}`} />

      <div><strong>Krok 2.</strong> Podstawiamy dane:</div>
      <BlockMath math={`\\sin\\beta = \\dfrac{${b}\\,\\sin(${alpha}^\\circ)}{${a}} = \\dfrac{${b}\\,\\big(${sA_latex}\\big)}{${a}} \\approx ${formatDecimalLatex(sinB, 3)}`} />

    <div><strong>Krok 3.</strong> Odczytujemy kąt <InlineMath math={'\\beta'} /> z tabeli wartości funkcji trygonometrycznych:</div>
    <BlockMath math={`\\sin\\beta \\approx ${formatDecimalLatex(sinB, 3)} \\quad\\Rightarrow\\quad \\beta \\approx ${Math.round(beta)}^\\circ`} />

    <div><strong>Krok 4.</strong> Trzeci kąt z sumy kątów:</div>
    <BlockMath math={`\\gamma = 180^\\circ - \\alpha - \\beta = 180^\\circ - ${alpha}^\\circ - ${Math.round(beta)}^\\circ = ${Math.round(gamma)}^\\circ`} />

    <div style={{ marginTop: "6px" }}>
      <strong>Odpowiedź:</strong>
      <BlockMath math={`\\sin\\beta \\approx ${formatDecimalLatex(sinB, 3)},\\quad \\beta \\approx ${Math.round(beta)}^\\circ,\\quad \\gamma \\approx ${Math.round(gamma)}^\\circ`} />
    </div>


      <div style={{ fontSize: "0.9em", marginTop: "6px", opacity: 0.85 }}>
        <em>Uwaga:</em> przypadek <strong>SSA</strong> (dwa boki i kąt <em>nieprzyległy</em>) 
        <strong> nie jest zasadą przystawania trójkątów</strong> – czasem mogą istnieć <strong>dwa</strong> różne rozwiązania
        (β ostry lub rozwarty). W tym zadaniu <strong>unikamy</strong> takich sytuacji: generator dobiera dane tak,
        aby rozwiązanie było jednoznaczne (bierzemy ostry <InlineMath math={'\\beta'} /> i mamy <InlineMath math={'\\gamma > 0'} />).
      </div>
    </div>
  );
}

/* =======================  USER ANSWER  ======================= */

function formatRootFracLatex(raw) {
  let s = String(raw ?? "").trim();
  if (!s) return "";
  const m = s.match(/^\s*([+-]?\d+)\s+(\d+)\/(\d+)\s*$/);
  if (m) return `${m[1]}\\;\\dfrac{${m[2]}}{${m[3]}}`;
  if (/^[+-]?\d+[.,]\d+$/.test(s.replace(/\s+/g, ""))) {
    return s.replace(/\s+/g, "").replace(".", "{,}");
  }
  s = s.replace(/\u2212/g, "-").replace(/\s+/g, "").replace(/sqrt\(([^)]+)\)/g, "\\sqrt{$1}");
  const slash = s.indexOf("/");
  if (slash > -1) {
    const num = s.slice(0, slash);
    const den = s.slice(slash + 1);
    return `\\dfrac{${num}}{${den}}`;
  }
  return s;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;
  const sinbRaw = typeof userAnswer.sinb === "object" ? userAnswer.sinb.raw : userAnswer.sinb;
  const betaRaw = typeof userAnswer.beta === "object" ? userAnswer.beta.raw : userAnswer.beta;
  const gammaRaw = typeof userAnswer.gamma === "object" ? userAnswer.gamma.raw : userAnswer.gamma;

  const sinbLatex = formatRootFracLatex(sinbRaw);
  const betaLatex = formatRootFracLatex(betaRaw);
  const gammaLatex = formatRootFracLatex(gammaRaw);

  return (
    <div style={{ marginTop: "6px", marginBottom: "6px" }}>
      <InlineMath math={`\\displaystyle \\sin\\beta = ${sinbLatex || '?'}`} />
      <span style={{ margin: "0 10px" }} />
      <InlineMath math={`\\displaystyle \\beta = ${betaLatex || '?'}^\\circ`} />
      <span style={{ margin: "0 10px" }} />
      <InlineMath math={`\\displaystyle \\gamma = ${gammaLatex || '?'}^\\circ`} />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
